import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CutterHealthCard from './CutterHealthCard';
import DeviceOverviewTable from './DeviceOverviewTable';
import {
  DeviceStatusChart,
  ProductionSummaryChart,
  TopMachinesChart,
} from './DashboardCharts';
import { getDashboard, getAllDevices } from '../../api';

/**
 * MainDashboard
 * Enterprise-grade Industrial IoT Fleet Monitoring Dashboard.
 * Consumes strictly live device telemetry and backend statistics.
 */
const MainDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Access Control: Restricted to isAdmin: true only and dealer field must be null */
  const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
  const isDealer = sessionStorage.getItem('isDealer') === 'true';
  const dealerRaw = sessionStorage.getItem('dealer');
  const hasDealer = isDealer || Boolean(dealerRaw && dealerRaw !== 'null' && dealerRaw !== 'undefined' && dealerRaw !== '');
  const canAccessDashboard = isAdmin && !hasDealer;

  // Fetch live telemetry from backend
  const fetchTelemetry = useCallback(async () => {
    setError(null);

    try {
      const [dashRes, devicesRes] = await Promise.allSettled([
        getDashboard(),
        getAllDevices(),
      ]);

      let hasSuccess = false;

      if (dashRes.status === 'fulfilled' && dashRes.value && !dashRes.value.error) {
        setDashboardData(dashRes.value);
        hasSuccess = true;
      }

      if (devicesRes.status === 'fulfilled' && Array.isArray(devicesRes.value)) {
        setDevices(devicesRes.value);
        hasSuccess = true;
      }

      if (!hasSuccess) {
        // If neither endpoint succeeded
        if (dashRes.status === 'rejected' && devicesRes.status === 'rejected') {
          setError('Unable to retrieve telemetry data from the IoT gateway.');
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard telemetry:', err);
      setError('Communication error with device telemetry server.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and 30-second live polling interval
  useEffect(() => {
    if (!canAccessDashboard) return;

    fetchTelemetry();

    const interval = setInterval(() => {
      fetchTelemetry();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchTelemetry, canAccessDashboard]);

  // Derived real fleet statistics
  const totalDevices = dashboardData?.totalDevices ?? (devices.length > 0 ? devices.length : null);
  const onlineDevices = dashboardData?.onlineDevices ?? (devices.length > 0 ? devices.filter(d => d.isActive).length : null);
  const offlineDevices = dashboardData?.offlineDevices ?? (devices.length > 0 ? devices.filter(d => !d.isActive).length : null);

  const onlineRate = totalDevices && totalDevices > 0 && onlineDevices != null
    ? Math.round((onlineDevices / totalDevices) * 1000) / 10
    : null;

  // Real total cuttings calculation across devices or from 7-day data
  const totalCuttings = useMemo(() => {
    if (devices.length > 0) {
      const sum = devices.reduce((acc, d) => acc + (Number(d.data?.keycount) || 0), 0);
      if (sum > 0) return sum;
    }
    if (dashboardData?.data && Array.isArray(dashboardData.data)) {
      const sum = dashboardData.data.reduce((acc, d) => acc + (Number(d.keycount) || 0), 0);
      if (sum > 0) return sum;
    }
    return null;
  }, [devices, dashboardData]);

  // Dynamic unique locations from real devices
  const locations = useMemo(() => {
    const locSet = new Set();
    devices.forEach((d) => {
      if (d.owner?.city) locSet.add(d.owner.city);
      else if (d.dealer?.name) locSet.add(d.dealer.name);
    });
    return Array.from(locSet).sort();
  }, [devices]);


  // Dynamic Top 10 Machines
  const topMachines = useMemo(() => {
    if (devices.length > 0) {
      const sorted = [...devices]
        .filter((d) => d.data && Number(d.data.keycount) > 0)
        .sort((a, b) => (Number(b.data?.keycount) || 0) - (Number(a.data?.keycount) || 0));
      if (sorted.length > 0) {
        return sorted.slice(0, 10).map((d) => ({
          id: d.deviceId || d.machSerial,
          count: Number(d.data?.keycount) || 0,
          dealer: d.dealer?.name || d.owner?.city || '',
        }));
      }
    }
    if (dashboardData?.top5devices && Array.isArray(dashboardData.top5devices) && dashboardData.top5devices.length > 0) {
      return dashboardData.top5devices;
    }
    return [];
  }, [dashboardData, devices]);

  // Real Fleet Cutter Health Calculation (Computed only when cutter telemetry exists)
  const fleetCutterHealth = useMemo(() => {
    const validFlat = [];
    const validBit = [];

    devices.forEach((d) => {
      const flatLife = Number(d.flatCutterHealth) || 5000;
      const bitLife = Number(d.bitCutterHealth) || 70;
      const flatCuts = Number(d.data?.flatcut);
      const bitCuts = Number(d.data?.bitcut);

      if (!isNaN(flatCuts) && flatCuts > 0) {
        const remainingPct = Math.max(0, Math.min(100, Math.round((1 - flatCuts / flatLife) * 100)));
        validFlat.push(remainingPct);
      }
      if (!isNaN(bitCuts) && bitCuts > 0) {
        const remainingPct = Math.max(0, Math.min(100, Math.round((1 - bitCuts / bitLife) * 100)));
        validBit.push(remainingPct);
      }
    });

    const buildHealthObj = (arr) => {
      if (arr.length === 0) return null;
      const avg = Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
      const healthy = arr.filter((p) => p >= 75).length;
      const warning = arr.filter((p) => p >= 50 && p < 75).length;
      const critical = arr.filter((p) => p < 50).length;
      const status = avg >= 80 ? 'Optimal' : avg >= 60 ? 'Good' : avg >= 40 ? 'Warning' : 'Critical';
      return { percentage: avg, healthy, warning, critical, status };
    };

    return {
      flat: buildHealthObj(validFlat),
      bit: buildHealthObj(validBit),
    };
  }, [devices]);

  if (!canAccessDashboard) {
    return (
      <div className="bg-[#f8fafc] min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 text-rose-600 flex items-center justify-center mb-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-800">Access Restricted</h2>
        <p className="text-xs text-slate-500 max-w-sm mt-1">
          Dashboard access is restricted to administrative accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#f8fafc] min-h-screen text-slate-800 font-sans antialiased selection:bg-blue-600 selection:text-white pb-12">
      <main className="p-3.5 sm:p-5 md:p-6 w-full space-y-4 sm:space-y-5">
        {/* Error Notification Banner */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl flex items-center justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-rose-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
            <button
              onClick={() => fetchTelemetry()}
              className="text-xs font-semibold text-rose-700 hover:text-rose-900 underline ml-3 shrink-0"
            >
              Retry Connection
            </button>
          </div>
        )}

        {/* =========================================================================
            1. TOP KPI ROW (5 COMPACT CARDS - Grid spanning 100% full width)
           ========================================================================= */}
        <section className="flex gap-3.5 w-full">
          {/* Card 1: Total Monitored Devices */}
          <div className="w-full bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total Fleet</span>
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 font-mono tracking-tight">
                    {loading && totalDevices == null ? (
                      <div className="h-7 w-12 bg-slate-200 rounded animate-pulse"></div>
                    ) : totalDevices != null ? (
                      totalDevices
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                    <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                    <line x1="6" y1="6" x2="6.01" y2="6"></line>
                    <line x1="6" y1="18" x2="6.01" y2="18"></line>
                  </svg>
                </div>
              </div>
              <div className="text-[11px] font-medium text-slate-400 mt-2.5 truncate">
                {locations.length > 0 ? `${locations.length} Connected Facilities` : 'Registered Machines'}
              </div>
            </div>

            {/* Card 2: Online Devices */}
            <div className="w-full bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Online Devices</span>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-2xl sm:text-3xl font-bold text-emerald-600 font-mono tracking-tight">
                      {loading && onlineDevices == null ? (
                        <div className="h-7 w-10 bg-slate-200 rounded animate-pulse"></div>
                      ) : onlineDevices != null ? (
                        onlineDevices
                      ) : (
                        '-'
                      )}
                    </span>
                    {onlineRate != null && (
                      <span className="text-xs font-semibold text-emerald-600">
                        ({onlineRate}%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
                    <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                    <line x1="12" y1="20" x2="12.01" y2="20"></line>
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 mt-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span>Active Heartbeat</span>
              </div>
            </div>

            {/* Card 3: Offline Devices */}
            <div className="w-full bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Offline Devices</span>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-2xl sm:text-3xl font-bold text-slate-600 font-mono tracking-tight">
                      {loading && offlineDevices == null ? (
                        <div className="h-7 w-10 bg-slate-200 rounded animate-pulse"></div>
                      ) : offlineDevices != null ? (
                        offlineDevices
                      ) : (
                        '-'
                      )}
                    </span>
                    {totalDevices && totalDevices > 0 && offlineDevices != null && (
                      <span className="text-xs font-medium text-slate-400">
                        ({Math.round((offlineDevices / totalDevices) * 100)}%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 mt-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                <span>Disconnected / Dormant</span>
              </div>
            </div>

            {/* Card 4: Total Key Cuts */}
            <div className="w-full bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total Cuts</span>
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 font-mono tracking-tight">
                    {loading && totalCuttings == null ? (
                      <div className="h-7 w-20 bg-slate-200 rounded animate-pulse"></div>
                    ) : totalCuttings != null ? (
                      totalCuttings.toLocaleString()
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="6" cy="6" r="3"></circle>
                    <circle cx="6" cy="18" r="3"></circle>
                    <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                    <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                  </svg>
                </div>
              </div>
              <div className="text-[11px] font-medium text-slate-400 mt-2.5">
                Production Telemetry Volume
              </div>
            </div>

            {/* Card 5: Fleet Availability */}
            <div className="w-full bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs hover:shadow-sm transition-all flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Availability</span>
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 font-mono tracking-tight">
                    {onlineRate != null ? `${onlineRate}%` : '-'}
                  </div>
                </div>
                <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-[11px] font-medium text-slate-400 mt-2.5">
                Fleet Operational Uptime
              </div>
            </div>
          </section>

        {/* =========================================================================
            2. CHARTS & CUTTER HEALTH MONITORING ROW
               (Renders conditionally strictly when live data exists)
           ========================================================================= */}
        <section
          className="flex gap-3"
        >
          {/* Chart 1: Fleet Connectivity Donut */}
          <div className="w-full h-full">
            <DeviceStatusChart 
              online={onlineDevices || 0}
              offline={offlineDevices || 0}
            />
          </div>

          {/* Chart 2: 7-Day Production / Cutting Trend */}
          <div className="w-full h-full">
            <ProductionSummaryChart
              trendData={dashboardData?.data || []}
              totalCuttings={totalCuttings}
            />
          </div>

          {/* Card 3: Flat Cutter Health (Render only if real cutter telemetry exists) */}
          {fleetCutterHealth.flat && (
            <div className="w-full h-full">
              <CutterHealthCard
                title="Flat Cutter Health"
                data={fleetCutterHealth.flat}
                subtitle="Fleet-wide tool wear indicator"
                ratedLife={5000}
              />
            </div>
          )}

          {/* Card 4: Bit Cutter Health */}
          {fleetCutterHealth.bit && (
            <div className="w-full h-full">
              <CutterHealthCard
                title="Bit Cutter Health"
                data={fleetCutterHealth.bit}
                subtitle="Precision cutter tool wear"
                ratedLife={70}
              />
            </div>
          )}
        </section>

        {/* =========================================================================
            3. FLEET OVERVIEW & TELEMETRY TABLE + TOP 10 MACHINES
           ========================================================================= */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start w-full">
          {/* Left / Main: Connected Devices Fleet Table (5 devices per page) */}
          <div className={`h-full ${topMachines.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12'}`}>
            <DeviceOverviewTable
              devices={devices}
              loading={loading}
            />
          </div>

          {/* Right: Top 10 Machines by Cuttings */}
          {topMachines.length > 0 && (
            <div className="lg:col-span-4">
              <TopMachinesChart machines={topMachines} />
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MainDashboard;
